// Routines — what a day needs, named so you can apply it again.
//
// "Regular Weekday", "Sunday", "Show Week". Each is a list of jobs: a kind of
// work, its hours, and how many people it takes. Feeding twenty horses takes
// two, and until this existed the only place that fact lived was in somebody's
// head — so a Thursday staffed by one looked exactly like a Thursday staffed
// right.
//
// This records DEMAND. It puts nobody on and tells nobody anything. Applying a
// routine to a day is done from the roster, where you can see what happens.
//
// Edited whole, like the availability pattern builder: a routine is a short
// list read as one thing, and saving line by line would let a barn reach a
// half-saved day shape.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { colorClass } from '../../lib/lessonTypes';
import { IconButton, PencilIcon } from '../../components/ui/icons';
import TimeSelect from '../../components/ui/TimeSelect';
import { timeRange, minutesBetween, formatMinutes } from '../../lib/shifts';
import WeekdayMap from '../../components/schedule/WeekdayMap';

export default function RoutinesPage() {
  const { activeRole } = useAuth();
  const canEdit = activeRole === 'owner' || activeRole === 'manager';
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // routine object, or 'new'
  const [retiring, setRetiring] = useState(null);
  const [err, setErr] = useState('');

  const routinesQ = useQuery({
    queryKey: ['routines', 'all'],
    queryFn: () => api.get('/routines?all=1'),
    enabled: canEdit,
    retry: false,
  });

  const typesQ = useQuery({
    queryKey: ['shift-types'],
    queryFn: () => api.get('/shift-types'),
    enabled: canEdit,
    retry: false,
  });

  const weekdaysQ = useQuery({
    queryKey: ['routine-weekdays'],
    queryFn: () => api.get('/routines/weekdays'),
    enabled: canEdit,
    retry: false,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['routines'] });
    qc.invalidateQueries({ queryKey: ['routine-weekdays'] });
    // The roster embeds coverage worked out from these, so it has to refetch or
    // a day keeps reporting against a plan that has changed.
    qc.invalidateQueries({ queryKey: ['shifts'] });
  };

  const save = useMutation({
    mutationFn: ({ id, ...body }) => (id ? api.put(`/routines/${id}`, body) : api.post('/routines', body)),
    onSuccess: () => { setEditing(null); setErr(''); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  const retire = useMutation({
    mutationFn: (id) => api.del(`/routines/${id}`),
    onSuccess: () => { setRetiring(null); setErr(''); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  const saveWeek = useMutation({
    // Saving the map and planning against it are one action from where somebody
    // is standing: they set the week to see the week planned. Two requests, but
    // never one without the other — a saved map that hasn't run yet looks
    // exactly like a map that doesn't work.
    mutationFn: async (map) => {
      await api.put('/routines/weekdays', { map });
      return api.post('/routines/plan-ahead', {});
    },
    onSuccess: () => { setErr(''); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  const restore = useMutation({
    // PUT replaces the lines wholesale, so restoring has to send the ones the
    // routine already has. Sending nothing would bring it back empty.
    mutationFn: (r) =>
      api.put(`/routines/${r.id}`, {
        name: r.name,
        notes: r.notes,
        active: true,
        lines: r.lines.map((l) => ({
          shiftTypeId: l.shiftTypeId,
          startTime: l.startTime,
          endTime: l.endTime,
          peopleNeeded: l.peopleNeeded,
        })),
      }),
    onSuccess: () => { setErr(''); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  if (!canEdit) {
    return (
      <div className="max-w-3xl mx-auto p-4">
        <div className="card p-8 text-center text-ink-500">Routines are set by an owner or manager.</div>
      </div>
    );
  }

  const all = routinesQ.data?.routines ?? [];
  const active = all.filter((r) => r.active);
  const retired = all.filter((r) => !r.active);
  // `shiftTypes`, not `types`. GET /shift-types names the list after itself,
  // the way GET /lesson-types does; `types` is what the ROSTER payload calls
  // its embedded copy. Reading the roster's key off the settings endpoint gave
  // an empty list every time — so this page said "there are no shift types yet"
  // to a barn with six of them, and the picker in the routine form had nothing
  // in it either.
  const types = typesQ.data?.shiftTypes ?? [];

  return (
    <div className="max-w-3xl mx-auto p-4 pb-24 sm:pb-8">
      <header className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Routines</h1>
          <p className="text-sm text-ink-500">
            What a day needs, and how many people each job takes. Apply one from the roster and it
            tells you when you&apos;re short.
          </p>
        </div>
        {!editing && (
          <button onClick={() => setEditing('new')} className="btn-primary shrink-0">
            + Add Routine
          </button>
        )}
      </header>

      {err && (
        <p className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700 mb-3">{err}</p>
      )}

      {types.length === 0 && !typesQ.isLoading && (
        <p className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 mb-3">
          There are no shift types yet, and a routine is built out of them. Add some under More → Shift
          types first.
        </p>
      )}

      {editing === 'new' && (
        <RoutineForm
          types={types}
          saving={save.isPending}
          onCancel={() => setEditing(null)}
          onSave={(body) => save.mutate(body)}
        />
      )}

      {routinesQ.isLoading ? (
        <div className="card p-8 text-center text-ink-400">Loading…</div>
      ) : (
        <ul className="space-y-3">
          {active.map((r) =>
            editing?.id === r.id ? (
              <li key={r.id}>
                <RoutineForm
                  initial={r}
                  types={types}
                  saving={save.isPending}
                  onCancel={() => setEditing(null)}
                  onSave={(body) => save.mutate({ id: r.id, ...body })}
                />
              </li>
            ) : retiring === r.id ? (
              <li key={r.id} className="card p-4 bg-ink-50">
                <p className="text-sm text-ink-700">
                  Retire <strong>{r.name}</strong>? It stops being offered when you plan days.{' '}
                  Days already planned from it keep exactly what they needed — applying copies the
                  jobs onto the day rather than pointing back here.
                </p>
                <div className="mt-3 flex gap-2 justify-end">
                  <button className="btn btn-secondary" onClick={() => setRetiring(null)}>Keep it</button>
                  <button
                    className="btn bg-rose-600 text-white hover:bg-rose-700"
                    disabled={retire.isPending}
                    onClick={() => retire.mutate(r.id)}
                  >
                    Retire
                  </button>
                </div>
              </li>
            ) : (
              <RoutineCard
                key={r.id}
                routine={r}
                busy={save.isPending || retire.isPending}
                onEdit={() => setEditing(r)}
                onRetire={() => setRetiring(r.id)}
              />
            ),
          )}
          {active.length === 0 && (
            <li className="card p-6 text-center text-sm text-ink-400">
              No routines yet. One that says &quot;morning feed takes two people&quot; is enough to start.
            </li>
          )}
        </ul>
      )}

      {active.length > 0 && (
        <div className="mt-4">
          <WeekdayMap
            routines={active}
            value={weekdaysQ.data?.weekdays ?? []}
            plannedThrough={weekdaysQ.data?.plannedThrough ?? null}
            saving={saveWeek.isPending}
            onSave={(map) => saveWeek.mutate(map)}
          />
        </div>
      )}

      {retired.length > 0 && (
        <section className="mt-6">
          <h2 className="text-sm font-medium text-ink-500 mb-2">Retired</h2>
          <ul className="card divide-y divide-ink-100">
            {retired.map((r) => (
              <li key={r.id} className="p-3 flex items-center justify-between gap-3">
                <span className="text-ink-500 truncate">
                  {r.name}
                  <span className="text-xs text-ink-400 ml-2">
                    {r.lines.length} {r.lines.length === 1 ? 'job' : 'jobs'}
                  </span>
                </span>
                <button
                  onClick={() => restore.mutate(r)}
                  disabled={restore.isPending}
                  className="text-sm text-ink-600 hover:text-ink-900 underline shrink-0"
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function RoutineCard({ routine, busy, onEdit, onRetire }) {
  const people = routine.lines.reduce((n, l) => n + l.peopleNeeded, 0);
  const hours = routine.lines.reduce(
    (n, l) => n + minutesBetween(l.startTime, l.endTime) * l.peopleNeeded,
    0,
  );

  return (
    <li className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-medium text-ink-900">{routine.name}</h3>
          <p className="text-sm text-ink-500">
            {/* People-slots, not bodies: two jobs needing one person each need
                two unless somebody covers both, and this number is about the
                plan rather than the payroll. */}
            {routine.lines.length} {routine.lines.length === 1 ? 'job' : 'jobs'} · {people} people ·{' '}
            {formatMinutes(hours)} of work
          </p>
          {routine.notes && <p className="text-sm text-ink-400 mt-1">{routine.notes}</p>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <IconButton icon={PencilIcon} label={`Edit ${routine.name}`} onClick={onEdit} disabled={busy} />
          <button
            onClick={onRetire}
            disabled={busy}
            className="text-sm text-ink-500 hover:text-rose-700"
          >
            Retire
          </button>
        </div>
      </div>

      <ul className="mt-3 rounded-md border border-ink-200 divide-y divide-ink-100 overflow-hidden">
        {routine.lines.map((l) => (
          <li key={l.id} className="flex items-center gap-3 px-3 py-2 text-sm">
            <span className={'w-2.5 h-2.5 rounded-full shrink-0 ' + colorClass(l.color, 'dot')} />
            <span className="font-medium text-ink-800 min-w-0 truncate">{l.typeName || 'Unspecified'}</span>
            <span className="text-ink-500 tabular-nums">{timeRange(l.startTime, l.endTime)}</span>
            <span className="ml-auto text-ink-600 shrink-0">
              {l.peopleNeeded} {l.peopleNeeded === 1 ? 'person' : 'people'}
            </span>
          </li>
        ))}
      </ul>
    </li>
  );
}

function RoutineForm({ initial, types, saving, onCancel, onSave }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [lines, setLines] = useState(() =>
    initial?.lines?.length
      ? initial.lines.map((l) => ({ ...l, key: l.id }))
      : [blankLine(types[0])],
  );

  function blankLine(type) {
    return {
      key: Math.random().toString(36).slice(2),
      shiftTypeId: type?.id ?? '',
      typeName: type?.name ?? '',
      startTime: type?.startTime || '06:00',
      endTime: type?.endTime || '09:00',
      peopleNeeded: 1,
    };
  }

  const set = (key, patch) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const bad = lines.some((l) => minutesBetween(l.startTime, l.endTime) === 0);
  const canSave = name.trim() && lines.length > 0 && !bad && !saving;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSave) return;
        onSave({
          name: name.trim(),
          notes: notes.trim() || null,
          lines: lines.map((l) => ({
            shiftTypeId: l.shiftTypeId || null,
            startTime: l.startTime,
            endTime: l.endTime,
            peopleNeeded: Number(l.peopleNeeded) || 1,
          })),
        });
      }}
      className="card p-4 space-y-4"
    >
      <div>
        <label htmlFor="rt-name" className="block text-sm font-medium text-ink-700 mb-1">Name</label>
        <input
          id="rt-name"
          className="input w-full"
          placeholder="Regular Weekday"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>

      <div>
        <span className="block text-sm font-medium text-ink-700 mb-1">What the day needs</span>
        <ul className="space-y-2">
          {lines.map((l) => (
            <li key={l.key} className="rounded-md border border-ink-200 p-3 flex flex-wrap items-end gap-3">
              <div className="min-w-[10rem] flex-1">
                <label className="block text-xs text-ink-400 mb-1" htmlFor={`rt-type-${l.key}`}>Work</label>
                <select
                  id={`rt-type-${l.key}`}
                  className="input w-full"
                  value={l.shiftTypeId}
                  onChange={(e) => {
                    const t = types.find((x) => x.id === e.target.value);
                    // Picking the work fills in its usual hours — that is what a
                    // shift type is for. Only when they haven't been changed
                    // from the last type's defaults would be fiddlier than it is
                    // worth; a routine is edited once and read for months.
                    set(l.key, {
                      shiftTypeId: e.target.value,
                      typeName: t?.name ?? '',
                      startTime: t?.startTime || l.startTime,
                      endTime: t?.endTime || l.endTime,
                    });
                  }}
                >
                  <option value="">Unspecified</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-ink-400 mb-1" htmlFor={`rt-from-${l.key}`}>From</label>
                <TimeSelect
                  label="From"
                  id={`rt-from-${l.key}`}
                  value={l.startTime}
                  onChange={(v) => set(l.key, { startTime: v })}
                />
              </div>
              <div>
                <label className="block text-xs text-ink-400 mb-1" htmlFor={`rt-to-${l.key}`}>To</label>
                <TimeSelect
                  label="To"
                  id={`rt-to-${l.key}`}
                  value={l.endTime}
                  onChange={(v) => set(l.key, { endTime: v })}
                />
              </div>
              <div className="w-24">
                <label className="block text-xs text-ink-400 mb-1" htmlFor={`rt-n-${l.key}`}>People</label>
                <input
                  id={`rt-n-${l.key}`}
                  type="number"
                  min="1"
                  max="20"
                  className="input w-full"
                  value={l.peopleNeeded}
                  onChange={(e) => set(l.key, { peopleNeeded: e.target.value })}
                />
              </div>
              <button
                type="button"
                onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                disabled={lines.length === 1}
                className="text-sm text-ink-400 hover:text-rose-700 disabled:opacity-40 pb-2"
                aria-label="Remove this job"
              >
                Remove
              </button>
              {minutesBetween(l.startTime, l.endTime) === 0 && (
                <p className="w-full text-sm text-rose-600">The end is before the start.</p>
              )}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setLines((prev) => [...prev, blankLine(types[0])])}
          className="mt-2 w-full rounded-lg border border-dashed border-ink-200 py-2 text-sm text-ink-400 hover:text-ink-700 hover:border-ink-300"
        >
          + Add a job
        </button>
      </div>

      <div>
        <label htmlFor="rt-notes" className="block text-sm font-medium text-ink-700 mb-1">Notes</label>
        <input
          id="rt-notes"
          className="input w-full"
          placeholder="Show week — feed an hour early, nobody on lessons"
          value={notes}
          maxLength={200}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <div className="flex gap-2">
        <button type="submit" disabled={!canSave} className="btn-primary">
          {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Routine'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-ink-500 hover:text-ink-800 px-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

function friendly(e) {
  const code = e?.body?.error || e?.message;
  switch (code) {
    case 'name_taken': return 'A routine with that name already exists.';
    case 'missing_name': return 'Give the routine a name.';
    case 'no_lines': return 'A routine needs at least one job in it.';
    case 'too_many_lines': return 'That is more jobs than a routine can hold.';
    case 'invalid_time': return "One of those times didn't read as a time.";
    case 'end_before_start': return 'One of those jobs ends before it starts.';
    case 'invalid_people': return 'A job needs between 1 and 20 people.';
    case 'unknown_shift_type': return 'One of those kinds of work no longer exists.';
    case 'not_allowed': return 'Only an owner or manager can change routines.';
    case 'not_found': return 'That routine is already gone.';
    default: {
      if (!code && !e?.status) return 'No response from the server. Check your connection.';
      return `That didn't save — ${code || 'no reason given'}${e?.status ? ` (${e.status})` : ''}.`;
    }
  }
}
